/**
 * Speaker UI controller: mic + Gemini session + room broadcast
 */
import { AudioRecorder } from "../audio/recorder";
import { AudioPlayer } from "../audio/player";
import { LiveSession } from "../live/session";
import { RoomClient } from "../room/client";
import { LANGUAGES, API_BASE } from "../config";
import { TranscriptAccumulator } from "./transcript";

interface SpeakerElements {
  sourceLang: HTMLSelectElement;
  targetLang: HTMLSelectElement;
  startBtn: HTMLButtonElement;
  stopBtn: HTMLButtonElement;
  statusEl: HTMLElement;
  inputText: HTMLElement;
  outputText: HTMLElement;
  audienceLink: HTMLElement;
}

export class SpeakerUI {
  private recorder = new AudioRecorder();
  private player = new AudioPlayer();
  private liveSession: LiveSession | null = null;
  private roomClient: RoomClient | null = null;
  private roomId: string;
  private speakerKey: string;
  private inputTranscript: TranscriptAccumulator;
  private outputTranscript: TranscriptAccumulator;

  constructor(private els: SpeakerElements, roomId: string, speakerKey: string) {
    this.roomId = roomId;
    this.speakerKey = speakerKey;
    this.inputTranscript = new TranscriptAccumulator(els.inputText);
    this.outputTranscript = new TranscriptAccumulator(els.outputText);
    this.populateLanguages();
    this.bindEvents();
    this.showAudienceLink();
  }

  private populateLanguages(): void {
    for (const lang of LANGUAGES) {
      this.els.sourceLang.add(new Option(lang.name, lang.code));
      this.els.targetLang.add(new Option(lang.name, lang.code));
    }
    this.els.sourceLang.value = "en";
    this.els.targetLang.value = "ko";
  }

  private bindEvents(): void {
    this.els.startBtn.onclick = () => this.start();
    this.els.stopBtn.onclick = () => this.stop();
  }

  private showAudienceLink(): void {
    const url = `${location.origin}/room/${this.roomId}`;
    this.els.audienceLink.innerHTML = `Audience link: <a href="${url}" target="_blank">${url}</a>`;
  }

  private setStatus(msg: string, level: "info" | "warn" | "error" = "info"): void {
    this.els.statusEl.textContent = msg;
    this.els.statusEl.className = `status ${level}`;
  }

  private async start(): Promise<void> {
    this.els.startBtn.disabled = true;
    this.setStatus("Getting token...");

    try {
      // Get ephemeral token
      const tokenRes = await fetch(`${API_BASE}/api/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId: this.roomId, speakerKey: this.speakerKey }),
      });
      if (!tokenRes.ok) throw new Error("Failed to get token");
      const { token } = await tokenRes.json();

      // Connect to room
      this.setStatus("Connecting to room...");
      this.roomClient = new RoomClient(this.roomId, "speaker", this.speakerKey);
      await this.roomClient.connect();

      // Setup live session
      this.setStatus("Connecting to Gemini...");
      this.liveSession = new LiveSession({
        onStateChange: (state) => {
          if (state === "streaming") {
            this.setStatus("Streaming");
            this.els.stopBtn.disabled = false;
          } else if (state === "error") {
            this.setStatus("Error occurred", "error");
          }
        },
        onInputTranscript: (text, finished) => {
          this.inputTranscript.update(text, finished);
          this.roomClient?.send({ t: "in_text", text, finished });
        },
        onOutputTranscript: (text, finished) => {
          this.outputTranscript.update(text, finished);
          this.roomClient?.send({ t: "out_text", text, finished });
        },
        onOutputAudio: (b64) => {
          this.player.enqueue(b64);
          this.roomClient?.send({ t: "out_audio", b64, sr: 24000 });
        },
        onInterrupt: () => {
          this.player.clear();
          this.roomClient?.send({ t: "interrupt" });
        },
        onError: (err) => this.setStatus(err.message, "error"),
      });

      await this.liveSession.connect(
        token,
        this.els.sourceLang.value,
        this.els.targetLang.value
      );

      // Start audio
      this.player.start();
      this.recorder.onChunk = (pcm16) => this.liveSession?.sendAudio(pcm16);
      await this.recorder.start();
    } catch (err) {
      this.setStatus((err as Error).message, "error");
      this.els.startBtn.disabled = false;
    }
  }

  private stop(): void {
    this.els.stopBtn.disabled = true;
    this.recorder.stop();
    this.player.stop();
    this.liveSession?.disconnect();
    this.roomClient?.disconnect();
    this.setStatus("Stopped");
    this.els.startBtn.disabled = false;
  }
}

